import { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import type { Problem } from '../../api/model';
import {
  getUISubscribersGetSubscriberQueryKey,
  useUISubscribersDeleteSubscriber,
} from '../../api/subscribers-ui/subscribers-ui.ts';

type DeleteSubscriberButtonProps = {
  username: string;
  onSuccess: () => void;
};

export const DeleteSubscriberButton = ({ username, onSuccess }: DeleteSubscriberButtonProps) => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const queryClient = useQueryClient();
  const deleteSubscriber = useUISubscribersDeleteSubscriber();

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteSubscriber = () => {
    deleteSubscriber.mutate(
      { username },
      {
        onSuccess: async () => {
          setOpenDeleteDialog(false);
          queryClient.removeQueries({ queryKey: getUISubscribersGetSubscriberQueryKey(username) });
          onSuccess();
        },
        onError: (error) => {
          setOpenDeleteDialog(false);
          const problemError = error as unknown as Problem;
          setErrorMessage(
            problemError?.response?.data?.detail || 'An error occurred while deleting the subscriber.',
          );
        },
      },
    );
  };

  return (
    <>
      <Button variant="outlined" color="error" onClick={handleOpenDeleteDialog}>
        Delete
      </Button>

      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Subscriber</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete subscriber "{username}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleDeleteSubscriber}
            color="error"
            variant="contained"
            disabled={deleteSubscriber.isPending}
          >
            {deleteSubscriber.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(errorMessage)} onClose={() => setErrorMessage('')}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorMessage('')} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
